import React from 'react';
import { View, Text } from 'react-native';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { create_setup_intent } from 'features/reservation/api/payment';
import { APP_STRIPE_PUBLIC_KEY } from '@env';
import { useLanguage } from './LanguageContext';

interface Props {
  children: React.ReactNode;
}

export const StripeClientSecretContext = React.createContext<string | null>(
  null
);

export const StripeContainer: React.FC<Props> = ({ children }) => {
  const { currentLanguage } = useLanguage();
  const [stripePromise, setStripePromise] =
    React.useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const isStripeEnabled =
    APP_STRIPE_PUBLIC_KEY !== undefined && APP_STRIPE_PUBLIC_KEY !== '';

  React.useEffect(() => {
    if (!isStripeEnabled || !APP_STRIPE_PUBLIC_KEY) {
      return;
    }
    setStripePromise(loadStripe(APP_STRIPE_PUBLIC_KEY));
  }, [isStripeEnabled]);

  React.useEffect(() => {
    if (!isStripeEnabled || !stripePromise) return;
    const setupIntent = async (): void => {
      const data = await create_setup_intent();
      const item = data?.item;
      setClientSecret(item.client_secret);
    };
    setupIntent();
  }, [isStripeEnabled, stripePromise]);

  if (!isStripeEnabled) {
    return (
      <StripeClientSecretContext.Provider value={null}>
        {children}
      </StripeClientSecretContext.Provider>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <StripeClientSecretContext.Provider value={null}>
        <View>
          <Text>Loading...</Text>
        </View>
      </StripeClientSecretContext.Provider>
    );
  }

  const options = {
    clientSecret,
    locale: currentLanguage,
    paymentMethodOrder: ['card'],
  };

  return (
    <StripeClientSecretContext.Provider value={clientSecret}>
      <Elements stripe={stripePromise} options={options}>
        {children}
      </Elements>
    </StripeClientSecretContext.Provider>
  );
};
